/**
 * Slash-command picker for Creative Skills — primary list from
 * `GET /api/v1/skills/catalog` (parsed SKILL.md files under `.claude/skills`), merged with
 * legacy `GET /api/v1/chat/slash-skills` (gap-fill) and contextual smart tiles.
 * Activated when the composer line starts with "/".
 */
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Loader2, Zap } from 'lucide-react';
import { clsx } from 'clsx';
import {
  agentLabApi,
  skillsCatalogApi,
  type CatalogSkill,
  type CategoryTile,
  type SkillTile,
  type SlashSkillEntry,
  type TileUnion,
  ApiError,
} from '@/services/api';

const CAT_LABEL: Record<string, string> = {
  insight: 'Insight',
  creative: 'Creative',
  seo: 'SEO',
  cro: 'CRO',
  paid_ads: 'Paid Ads',
  display: 'Display',
};

export interface SlashSkillPick {
  skillId: string;
  prompt: string;
  signal?: string;
}

export interface UnifiedSlashRow {
  id: string;
  skill_id: string;
  title: string;
  subtitle: string;
  trigger_prompt: string;
  signal?: string;
  group: string;
}

export function parseCreativeSlashLine(
  value: string,
  cursorPos: number,
): { active: boolean; filter: string; lineStart: number; lineEnd: number } | null {
  const lineStart = value.slice(0, cursorPos).lastIndexOf('\n') + 1;
  const afterCursor = value.slice(cursorPos);
  const nlAfter = afterCursor.indexOf('\n');
  const lineRest = nlAfter === -1 ? afterCursor : afterCursor.slice(0, nlAfter);
  const lineEnd = cursorPos + lineRest.length;
  const fullLine = value.slice(lineStart, lineEnd);
  if (!fullLine.startsWith('/')) return null;
  return {
    active: true,
    filter: fullLine.slice(1),
    lineStart,
    lineEnd,
  };
}

function modesLabel(modes: string[]): string {
  if (!modes.length) return 'All modes';
  const labels = modes.map((m) => (m === 'explore' ? 'Explore' : m === 'plan' ? 'Plan' : 'Create'));
  return labels.join(' · ');
}

/** Build chat trigger text from global SKILL.md catalog metadata (same source as Skills Catalog page). */
function buildCatalogTriggerPrompt(
  s: CatalogSkill,
  clientUid: string,
  personaLabel: string,
): string {
  const name = (s.display_name || s.name || s.skill_id).trim();
  const header =
    `Execute the "${name}" skill (skill_id: ${s.skill_id}) for client ${clientUid} and persona ${personaLabel}. ` +
    `Use NeuroGraph tools and Bonnard where the skill requires live data.`;
  const desc = (s.description || '').trim();
  const body = desc ? `\n\nSkill intent:\n${desc.slice(0, 4000)}` : '';
  return (header + body).slice(0, 8000);
}

function catalogSkillToRow(
  s: CatalogSkill,
  clientUid: string,
  personaId: string | null | undefined,
): UnifiedSlashRow {
  const personaLabel = personaId?.trim() || 'the selected persona';
  const sub = (s.description || '').trim();
  return {
    id: `skill-md-${s.skill_id}`,
    skill_id: s.skill_id,
    title: s.display_name || s.name,
    subtitle: sub.length > 180 ? `${sub.slice(0, 180)}…` : sub,
    trigger_prompt: buildCatalogTriggerPrompt(s, clientUid, personaLabel),
    group: `Skills catalog · ${s.category_label || CAT_LABEL[s.category] || s.category}`,
  };
}

/**
 * Merge global SKILL.md catalog (primary) with legacy slash-skills rows and smart tiles.
 * Catalog wins on duplicate skill_id vs legacy API; duplicate trigger_prompt strings dedupe globally.
 */
function mergeSlashSources(
  globalCatalogSkills: CatalogSkill[],
  legacySlash: SlashSkillEntry[],
  smartTiles: TileUnion[],
  smartMode: 'explore' | 'plan' | 'create',
  clientUid: string,
  personaId: string | null | undefined,
): UnifiedSlashRow[] {
  const seenTrigger = new Set<string>();
  const catalogSkillIds = new Set<string>();
  const rows: UnifiedSlashRow[] = [];

  const push = (r: UnifiedSlashRow) => {
    const k = r.trigger_prompt.trim();
    if (seenTrigger.has(k)) return;
    seenTrigger.add(k);
    rows.push(r);
  };

  for (const s of globalCatalogSkills) {
    const row = catalogSkillToRow(s, clientUid, personaId);
    catalogSkillIds.add(row.skill_id.toLowerCase());
    push(row);
  }

  for (const e of legacySlash) {
    if (catalogSkillIds.has(e.skill_id.toLowerCase())) {
      continue;
    }
    push({
      id: `cat-${e.id}`,
      skill_id: e.skill_id,
      title: e.skill_name,
      subtitle: e.pitch,
      trigger_prompt: e.trigger_prompt,
      group: `${CAT_LABEL[e.category] ?? e.category} · ${modesLabel(e.modes)}`,
    });
  }

  for (const t of smartTiles ?? []) {
    if (t.tile_type === 'category') {
      const c = t as CategoryTile;
      for (const sub of c.sub_skills) {
        push({
          id: `sig-${c.skill_id}-${sub.skill_id}-${sub.signal_label}`,
          skill_id: sub.skill_id,
          title: `${c.skill_name} › ${sub.skill_name}`,
          subtitle: sub.signal_label,
          trigger_prompt: sub.trigger_prompt,
          signal: sub.signal_label,
          group: 'Live signals',
        });
      }
    } else {
      const d = t as SkillTile;
      push({
        id: `smart-${d.skill_id}-${d.trigger_prompt.slice(0, 48)}`,
        skill_id: d.skill_id,
        title: d.skill_name,
        subtitle: d.pitch,
        trigger_prompt: d.trigger_prompt,
        group: `Suggested · ${smartMode}`,
      });
    }
  }

  return rows;
}

function filterRows(rows: UnifiedSlashRow[], q: string): UnifiedSlashRow[] {
  const s = q.trim().toLowerCase();
  if (!s) return rows;
  return rows.filter((r) => {
    const hay = [r.title, r.subtitle, r.skill_id, r.group].join(' ').toLowerCase();
    return hay.includes(s);
  });
}

export interface UseCreativeSlashMenuArgs {
  /** Textarea the user is typing in — used to position the menu. */
  composerRef: React.RefObject<HTMLTextAreaElement | null>;
  value: string;
  cursorPos: number;
  clientUid?: string | null;
  personaId?: string | null;
  /** Home mode — selects which smart-tiles slice merges alongside the full catalog. */
  mode?: 'explore' | 'plan' | 'create';
  disabled?: boolean;
  onValueChange: (next: string, cursorPos?: number) => void;
  onPick: (pick: SlashSkillPick) => void;
}

export interface UseCreativeSlashMenuResult {
  /** Merge into the textarea onKeyDown — returns true if the event was consumed. */
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => boolean;
  /** Render alongside the composer (fixed-position portal). */
  SlashMenuPortal: React.ReactNode;
}

export function useCreativeSlashMenu({
  composerRef,
  value,
  cursorPos,
  clientUid,
  personaId,
  mode = 'explore',
  disabled,
  onValueChange,
  onPick,
}: UseCreativeSlashMenuArgs): UseCreativeSlashMenuResult {
  const slash = useMemo(
    () => (disabled ? null : parseCreativeSlashLine(value, cursorPos)),
    [value, cursorPos, disabled],
  );

  const open = Boolean(slash?.active);
  const [rows, setRows] = useState<UnifiedSlashRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadErrorNotFound, setLoadErrorNotFound] = useState(false);
  const [catalogApiFailed, setCatalogApiFailed] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const filtered = useMemo(
    () => filterRows(rows, slash?.filter ?? ''),
    [rows, slash?.filter],
  );

  useEffect(() => {
    setHighlight(0);
  }, [slash?.filter, rows.length, open]);

  useEffect(() => {
    if (!open || !clientUid) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    setLoadErrorNotFound(false);
    setCatalogApiFailed(false);

    (async () => {
      let catalogSkills: CatalogSkill[] = [];
      try {
        const catRes = await skillsCatalogApi.list();
        catalogSkills = catRes.skills ?? [];
      } catch {
        if (!cancelled) setCatalogApiFailed(true);
      }

      let slashEntries: SlashSkillEntry[] = [];
      let slashRouteMissing = false;
      try {
        const slashRes = await agentLabApi.getSlashSkills(clientUid, personaId ?? undefined);
        slashEntries = slashRes.entries;
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) {
          slashRouteMissing = true;
        } else if (!cancelled) {
          setLoadError(true);
          setRows([]);
          return;
        }
      }

      let smartTiles: TileUnion[] = [];
      try {
        const smartRes = await agentLabApi.getSmartTiles(clientUid, personaId ?? undefined, 8, mode);
        smartTiles = smartRes.tiles ?? [];
      } catch {
        if (!cancelled && catalogSkills.length === 0 && slashEntries.length === 0 && !slashRouteMissing) {
          setLoadError(true);
          setRows([]);
          return;
        }
      }

      if (cancelled) return;

      const merged = mergeSlashSources(
        catalogSkills,
        slashEntries,
        smartTiles,
        mode,
        clientUid,
        personaId,
      );
      setRows(merged);
      if (slashRouteMissing) {
        setLoadErrorNotFound(true);
      }
      setLoadError(merged.length === 0);
    })()
      .catch(() => {
        if (!cancelled) {
          setLoadError(true);
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, clientUid, personaId, mode]);

  const [anchor, setAnchor] = useState({ top: 0, left: 0, width: 400 });

  useLayoutEffect(() => {
    if (!open) return;
    const el = composerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchor({
      top: r.bottom + 6,
      left: r.left,
      width: Math.min(440, r.width),
    });
  }, [open, value, cursorPos, composerRef]);

  const stripSlashLine = useCallback(() => {
    if (!slash) return;
    const next = `${value.slice(0, slash.lineStart)}${value.slice(slash.lineEnd)}`;
    onValueChange(next, slash.lineStart);
  }, [slash, value, onValueChange]);

  const pickRow = useCallback(
    (row: UnifiedSlashRow) => {
      stripSlashLine();
      onPick({
        skillId: row.skill_id,
        prompt: row.trigger_prompt,
        ...(row.signal ? { signal: row.signal } : {}),
      });
    },
    [stripSlashLine, onPick],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!open || disabled) return false;
      if (!slash) return false;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
        return true;
      }
      if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
        const row = filtered[highlight];
        if (row) {
          e.preventDefault();
          pickRow(row);
          return true;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        stripSlashLine();
        return true;
      }
      return false;
    },
    [open, disabled, slash, filtered, highlight, pickRow, stripSlashLine],
  );

  const Portal = useMemo(() => {
    if (!open) return null;

    const showNoClient = !clientUid;
    const list = showNoClient ? [] : filtered;

    return createPortal(
      <div
        className="fixed z-[100] rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden flex flex-col"
        style={{
          top: anchor.top,
          left: anchor.left,
          width: anchor.width,
          maxHeight: 320,
        }}
        role="listbox"
        aria-label="Creative skills"
      >
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/80 flex items-center gap-2">
          <Zap size={14} className="text-purple-500 shrink-0" />
          <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
            Skills · catalog + suggestions
          </span>
          {loading && <Loader2 size={12} className="animate-spin text-slate-400 ml-auto" />}
        </div>

        {showNoClient && (
          <div className="px-4 py-6 text-center text-sm text-slate-500">
            Select a client in the workbench to load skills — sourced from{' '}
            <code className="bg-slate-100 px-1 rounded text-[10px]">GET /api/v1/skills/catalog</code> plus live
            suggestions.
          </div>
        )}

        {!showNoClient && catalogApiFailed && rows.length > 0 && (
          <div className="px-3 py-2 text-[11px] text-sky-900 bg-sky-50 border-b border-sky-100 leading-snug">
            Global skills catalog unavailable — showing fallback slash list and live suggestions. Check{' '}
            <code className="bg-sky-100 px-1 rounded">GET /api/v1/skills/catalog</code>.
          </div>
        )}

        {!showNoClient && loadErrorNotFound && rows.length > 0 && (
          <div className="px-3 py-2 text-[11px] text-amber-900 bg-amber-50 border-b border-amber-100 leading-snug">
            Legacy <code className="bg-amber-100 px-1 rounded">GET /api/v1/chat/slash-skills</code> unavailable on this
            build — global catalog rows still apply when the catalog API succeeds.
          </div>
        )}

        {!showNoClient && loadError && loadErrorNotFound && rows.length === 0 && (
          <div className="px-4 py-5 text-sm text-amber-900 bg-amber-50 border-b border-amber-100 leading-relaxed">
            <span className="font-semibold">Core API is missing this route (404).</span>{' '}
            Restart the agent on port 8000 so it picks up{' '}
            <code className="text-xs bg-amber-100 px-1 rounded">GET /api/v1/chat/slash-skills</code>
            — e.g. <code className="text-xs bg-amber-100 px-1 rounded">./dev.sh restart api</code>
            — or deploy the latest backend. Until then, use ⌘K or the skill tiles below.
          </div>
        )}

        {!showNoClient && loadError && !loadErrorNotFound && (
          <div className="px-4 py-6 text-center text-sm text-rose-600">
            Could not load skills. Check your connection and try again.
          </div>
        )}

        {!showNoClient && !loadError && list.length === 0 && !loading && (
          <div className="px-4 py-6 text-center text-sm text-slate-400">No matching skills</div>
        )}

        {!showNoClient && !loadError && list.length > 0 && (
          <ul className="overflow-y-auto py-1">
            {list.map((row, i) => (
              <li key={row.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === highlight}
                  className={clsx(
                    'w-full text-left px-3 py-2.5 flex flex-col gap-0.5 transition-colors',
                    i === highlight ? 'bg-purple-50' : 'hover:bg-slate-50',
                  )}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pickRow(row);
                  }}
                >
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 truncate">
                    {row.group}
                  </span>
                  <span className="text-sm font-semibold text-slate-800 leading-snug">{row.title}</span>
                  <span className="text-xs text-slate-500 line-clamp-2">{row.subtitle}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>,
      document.body,
    );
  }, [
    open,
    anchor.top,
    anchor.left,
    anchor.width,
    clientUid,
    filtered,
    highlight,
    loading,
    loadError,
    loadErrorNotFound,
    catalogApiFailed,
    rows.length,
    pickRow,
  ]);

  return { handleKeyDown, SlashMenuPortal: Portal };
}
